import { Config } from '@/config';
import { CartAddRequestModel, CartEditRequestModel, CartItemRemoveRequestModel } from '@/models/CartRequestModel';
import { CartModel } from '@/models/CartModel';
import {
  CartAddRequestRawModel,
  CartEditRequestRawModel,
  CartItemRemoveRequestRawModel,
} from '@/api/api-raw-models/cart-request-raw-model';
import {
  mapCartAddRequest,
  mapCartEditRequest,
  mapCartItemRemoveRequest,
  mapCheckoutRequest,
} from '@/api/mappers/map-request';
import { CartRawModel } from '@/api/api-raw-models/cart-raw-model';
import { mapCart, mapCheckout } from '@/api/mappers/map-response';
import BaseAuthApiService from '@/api/base-auth-api-service';
import { CheckoutRequestModel } from '@/models/CheckoutRequestModel';
import { CheckoutModel } from '@/models/CheckoutModel';
import { CheckoutRequestRawModel } from '@/api/api-raw-models/checkout-request-raw-model';
import { CheckoutRawModel } from '@/api/api-raw-models/checkout-raw-model';

export class CartApiService extends BaseAuthApiService {
  private readonly cartPrefix = 'cart';

  public async cartInfo(): Promise<CartModel> {
    const url = `${Config.ApiUrl}/${this.cartPrefix}/info`;

    const res = await this.authorizedRequest(url, {
      method: 'GET',
    });

    return this.mapResponse<CartRawModel, CartModel>(res, mapCart);
  }

  public async addCart(data: CartAddRequestModel): Promise<CartModel> {
    const url = `${Config.ApiUrl}/${this.cartPrefix}/add`;

    const res = await this.authorizedRequest(url, {
      method: 'POST',
      body: JSON.stringify(
        this.mapRequest<CartAddRequestModel, CartAddRequestRawModel>(data, mapCartAddRequest),
      ),
    });

    return this.mapResponse<CartRawModel, CartModel>(res, mapCart);
  }

  public async editCart(data: CartEditRequestModel): Promise<CartModel> {
    const url = `${Config.ApiUrl}/${this.cartPrefix}/update`;

    const res = await this.authorizedRequest(url, {
      method: 'POST',
      body: JSON.stringify(
        this.mapRequest<CartEditRequestModel, CartEditRequestRawModel>(data, mapCartEditRequest),
      ),
    });

    return this.mapResponse<CartRawModel, CartModel>(res, mapCart);
  }

  public async removeCartItem(data: CartItemRemoveRequestModel): Promise<CartModel> {
    const url = `${Config.ApiUrl}/${this.cartPrefix}/remove`;

    const res = await this.authorizedRequest(url, {
      method: 'POST',
      body: JSON.stringify(
        this.mapRequest<CartItemRemoveRequestModel, CartItemRemoveRequestRawModel>(data, mapCartItemRemoveRequest),
      ),
    });

    return this.mapResponse<CartRawModel, CartModel>(res, mapCart);
  }

  public async emptyCart(): Promise<void> {
    const url = `${Config.ApiUrl}/${this.cartPrefix}/empty`;

    await this.authorizedRequest(url, {
      method: 'DELETE',
    });
  }

  public async addTip(type: 'dollar' | 'percent', tip: number): Promise<CartModel> {
    const url = `${Config.ApiUrl}/${this.cartPrefix}/tip`;

    const res = await this.authorizedRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        type,
        tip,
      }),
    });

    return this.mapResponse<CartRawModel, CartModel>(res, mapCart);
  }

  public async removeTip(type: 'dollar' | 'percent'): Promise<CartModel> {
    return this.addTip(type, 0);
  }

  public async checkout(data: CheckoutRequestModel): Promise<CheckoutModel> {
    const url = `${Config.ApiUrl}/${this.cartPrefix}/checkout`;

    const res = await this.authorizedRequest(url, {
      method: 'POST',
      body: JSON.stringify(
        this.mapRequest<CheckoutRequestModel, CheckoutRequestRawModel>(data, mapCheckoutRequest),
      ),
    });

    return this.mapResponse<CheckoutRawModel, CheckoutModel>(res, mapCheckout);
  }
}
